import {
  useEffect,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Icon, Skeleton, type IconName } from '@capsico/ui';
import { useToast } from '../hooks/useToast';
import { useLastErrorRef } from '../api/lastErrorRef';
import { useI18n } from '../i18n';
import { SupportRef } from './SupportRef';

/*
 * Customer-app kit (capsico-customer-app-ui, inheriting quick-commerce-ui): white cards with an
 * inset hairline, yellow CTAs with ink text (never white on yellow), black pill for "selected",
 * Poppins everywhere. Presentation only — no data or state machines live here.
 */

export function Card({
  children,
  className = '',
  flush = false,
}: {
  children: ReactNode;
  className?: string;
  /** No inner padding — for cards made of full-width rows. */
  flush?: boolean;
}) {
  return (
    <section
      className={`rounded-sheet bg-surface shadow-[inset_0_0_0_1px_var(--color-hair)] ${flush ? 'overflow-hidden' : 'p-4'} ${className}`}
    >
      {children}
    </section>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-3">
      <h2 className="text-base font-bold text-ink">{children}</h2>
      {action}
    </div>
  );
}

type Tone = 'brand' | 'dark' | 'outline' | 'ghost' | 'danger';

const TONE: Record<Tone, string> = {
  brand: 'bg-brand text-on-brand active:bg-brand-600',
  dark: 'bg-dark text-white',
  outline: 'bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-control)]',
  ghost: 'text-brand-ink',
  danger: 'bg-surface text-negative shadow-[inset_0_0_0_1px_var(--color-control)]',
};

export function Button({
  tone = 'brand',
  busy = false,
  full = false,
  size = 'md',
  children,
  className = '',
  disabled,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: Tone;
  busy?: boolean;
  full?: boolean;
  size?: 'sm' | 'md';
  children: ReactNode;
}) {
  const height = size === 'sm' ? 'h-9 px-3.5 text-xs' : 'h-12 px-5 text-sm';
  const off = disabled && tone === 'brand' ? '!bg-tile !text-ink-3' : 'disabled:opacity-50';
  return (
    <button
      type={type}
      disabled={disabled || busy}
      className={`inline-flex items-center justify-center gap-2 rounded-field font-semibold transition-transform active:scale-[0.98] ${height} ${TONE[tone]} ${off} ${full ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {busy && (
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  onClick,
  className = '',
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`inline-flex size-9 items-center justify-center rounded-full text-ink active:scale-95 ${className}`}
    >
      <Icon name={icon} size={20} />
    </button>
  );
}

/** Text field with the label above (form standard K). Placeholders are examples, never labels. */
export function Field({
  label,
  error,
  hint,
  className = '',
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | null;
  hint?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-medium text-ink-2">{label}</span>
      <input
        {...input}
        className={`h-11 w-full rounded-field bg-surface px-3 text-sm font-medium text-ink placeholder:font-normal placeholder:text-ink-3 focus:outline-none ${
          error
            ? 'shadow-[inset_0_0_0_1.5px_var(--color-negative)]'
            : 'shadow-[inset_0_0_0_1px_var(--color-field)] focus:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)]'
        }`}
      />
      {error ? (
        <span className="mt-1 block text-caption text-negative">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-caption text-ink-3">{hint}</span>
      ) : null}
    </label>
  );
}

/** Pill choice (J): active = black pill with white text; idle = white with an outline. */
export function Pill({
  active,
  onClick,
  children,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  icon?: IconName;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-xs transition-colors ${
        active
          ? 'bg-ink font-medium text-surface'
          : 'bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-control)]'
      }`}
    >
      {icon && <Icon name={icon} size={14} />}
      {children}
    </button>
  );
}

/** Filter chip that toggles on/off (selected = soft yellow tint with a brand ring). */
export function FilterChip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-medium ${
        on
          ? 'bg-brand-soft text-ink shadow-[inset_0_0_0_1.5px_var(--color-brand-600)]'
          : 'bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-control)]'
      }`}
    >
      {on && <Icon name="check" size={12} />}
      {children}
    </button>
  );
}

/** Large sliding switch (J). */
export function Toggle({
  on,
  onChange,
  label,
  disabled = false,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  /** Locked (shown in its state, cannot be changed) — e.g. order updates are always on. */
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative h-6.5 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-brand' : 'bg-control'}`}
    >
      <span
        className={`absolute top-0.75 size-5 rounded-full bg-white shadow-[inset_0_0_0_1px_var(--color-on-brand)] transition-transform ${on ? 'translate-x-5.25' : 'translate-x-0.75'}`}
      />
    </button>
  );
}

/** Veg / non-veg mark: square outline with a filled dot (green veg, red non-veg, amber egg). */
export function VegMark({ type, size = 14 }: { type: 'veg' | 'non_veg' | 'egg'; size?: number }) {
  const colour =
    type === 'veg' ? 'border-positive' : type === 'egg' ? 'border-warning' : 'border-negative';
  const dot = type === 'veg' ? 'bg-positive' : type === 'egg' ? 'bg-warning' : 'bg-negative';
  const label = type === 'veg' ? 'Veg' : type === 'egg' ? 'Contains egg' : 'Non-veg';
  return (
    <span
      role="img"
      aria-label={label}
      className={`inline-flex shrink-0 items-center justify-center rounded-tick border-[1.5px] bg-white ${colour}`}
      style={{ width: size, height: size }}
    >
      <span className={`rounded-full ${dot}`} style={{ width: size / 2.4, height: size / 2.4 }} />
    </span>
  );
}

export function Notice({
  tone,
  title,
  children,
  action,
  supportRef = false,
}: {
  tone: 'info' | 'warning' | 'negative' | 'positive' | 'brand';
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** W25: an error notice — show the support code of the failure when the server sent one. */
  supportRef?: boolean;
}) {
  const bg = {
    info: 'bg-info-bg',
    warning: 'bg-warning-bg',
    negative: 'bg-negative-bg',
    positive: 'bg-positive-bg',
    brand: 'bg-brand-soft',
  }[tone];
  return (
    <div className={`flex items-start gap-3 rounded-xl px-4 py-3 ${bg}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {children && <div className="mt-0.5 text-xs text-ink-2">{children}</div>}
        {supportRef && (
          <div className="mt-2">
            <LastSupportCode start />
          </div>
        )}
      </div>
      {action}
    </div>
  );
}

/** "We kept what you typed" strip (draft persistence standard). */
export function DraftBar({
  text,
  keepLabel,
  discardLabel,
  onKeep,
  onDiscard,
}: {
  text: string;
  keepLabel: string;
  discardLabel: string;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-brand-soft px-3 py-2.5">
      <Icon name="refresh" size={16} />
      <p className="min-w-0 flex-1 text-xs text-ink">{text}</p>
      <button type="button" onClick={onDiscard} className="text-xs font-semibold text-ink-2">
        {discardLabel}
      </button>
      <button type="button" onClick={onKeep} className="text-xs font-semibold text-brand-ink">
        {keepLabel}
      </button>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  supportRef = false,
}: {
  icon: IconName;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** W25: an error screen — show the support code of the failure when the server sent one. */
  supportRef?: boolean;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="inline-flex size-16 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
        <Icon name={icon} size={28} />
      </span>
      <p className="mt-4 text-title font-semibold text-ink">{title}</p>
      {children && <p className="mt-1 max-w-xs text-xs leading-5 text-ink-2">{children}</p>}
      {supportRef && (
        <div className="mt-4">
          <LastSupportCode />
        </div>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ListSkeleton({ rows = 3, tall = false }: { rows?: number; tall?: boolean }) {
  return (
    <div className="space-y-4" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="space-y-2">
          {tall && <Skeleton className="aspect-video w-full" rounded="card" />}
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

/** Bottom sheet with a dark floating × above it (pattern V). Esc and the scrim close it. */
export function Sheet({
  title,
  onClose,
  children,
  footer,
}: {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-label={title}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-scrim animate-[fade_160ms_ease-out]"
      />
      <div className="relative mx-auto flex max-h-sheet-max w-full max-w-120 flex-col animate-[rise_200ms_ease-out]">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="mx-auto mb-3 inline-flex size-10 items-center justify-center rounded-full bg-ink/80 text-surface"
        >
          <Icon name="x" size={18} />
        </button>
        <div className="flex min-h-0 flex-col rounded-t-sheet bg-surface pb-[env(safe-area-inset-bottom)]">
          {title && <h2 className="px-5 pt-5 text-base font-bold text-ink">{title}</h2>}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <div className="px-5 py-3 shadow-[inset_0_1px_0_var(--color-hair)]">{footer}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Sub-page header: back arrow + title (h-48). */
export function PageHeader({
  title,
  onBack,
  backLabel,
  action,
}: {
  title: string;
  onBack: () => void;
  backLabel: string;
  action?: ReactNode;
}) {
  return (
    <header className="sticky top-0 z-30 flex h-12 items-center gap-1 bg-surface px-2 shadow-[inset_0_-1px_0_var(--color-hair)]">
      <IconButton icon="chevron-left" label={backLabel} onClick={onBack} />
      <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{title}</h1>
      {action}
    </header>
  );
}

/** One toast at a time, above the bottom nav. */
export function Toasts() {
  const toast = useToast();
  if (!toast) return null;
  const tone =
    toast.tone === 'negative'
      ? 'bg-negative text-white'
      : toast.tone === 'positive'
        ? 'bg-positive text-white'
        : 'bg-ink text-surface';
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex justify-center px-4">
      <div
        role="status"
        className={`max-w-sm space-y-2 rounded-xl px-4 py-3 text-center text-sm font-medium ${tone}`}
      >
        <p>{toast.message}</p>
        {toast.ref && <SupportCode code={toast.ref} onDark />}
      </div>
    </div>
  );
}

/** W25: the support code chip with its translated labels. */
export function SupportCode({
  code,
  onDark = false,
  start = false,
}: {
  code: string;
  onDark?: boolean;
  start?: boolean;
}) {
  const { t } = useI18n();
  return (
    <SupportRef
      start={start}
      code={code}
      label={t('common.supportRef')}
      copyLabel={t('common.copyRef')}
      copiedLabel={t('common.refCopied')}
      onDark={onDark}
    />
  );
}

/** W25: the support code of the latest failed API call, for generic error screens. */
export function LastSupportCode({ start = false }: { start?: boolean }) {
  const ref = useLastErrorRef();
  return ref ? <SupportCode code={ref} start={start} /> : null;
}
